import React, { useState, useEffect, useRef, useCallback } from 'react';
import { T } from './constants';
import { Wave, OpenInAppPill } from './components';
import { useOpenApp } from './hooks';
import { fmt } from './utils';
import { SharedAudioDetail } from './types';
import { reportAudioPlay } from './api';

export default function PlayerPage({
  data,
  onBack,
  initialTime = 0,
}: {
  data: SharedAudioDetail;
  onBack: () => void;
  initialTime?: number;
}) {
  const [isPlaying, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(data.duration);
  const [isDragging, setIsDragging] = useState(false);
  const openApp = useOpenApp(data.video_id);

  const audioRef = useRef<HTMLAudioElement>(null);
  const barRef = useRef<HTMLDivElement>(null);

  const reportedRef = useRef(false);

  // Seek to initialTime and auto-play when the player opens
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = initialTime;
    setCur(initialTime);
    audio.play().catch((err) => {
      if (err?.name === 'NotAllowedError') {
        setPlaying(false);
      }
    });
  }, [initialTime]);

  // Wire audio events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCur(audio.currentTime);
    const onLoadedMeta = () => setDur(audio.duration || data.duration);
    const onPlay = () => {
      setPlaying(true);
      if (!reportedRef.current) {
        reportedRef.current = true;
        reportAudioPlay({ video_id: data.video_id, share_code: undefined }).catch(() => {});
      }
    };
    const onPause = () => setPlaying(false);
    const onEnded = () => setPlaying(false);

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMeta);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMeta);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  }, []);

  const updateScrub = useCallback((clientX: number) => {
    const audio = audioRef.current;
    const el = barRef.current;
    if (!audio || !el) return;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    audio.currentTime = dur * ratio;
  }, [dur]);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    updateScrub(e.clientX);
  }, [updateScrub]);

  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    setIsDragging(true);
    updateScrub(e.touches[0].clientX);
  }, [updateScrub]);

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e: MouseEvent) => updateScrub(e.clientX);
    const handleTouchMove = (e: TouchEvent) => updateScrub(e.touches[0].clientX);
    const handleUp = () => setIsDragging(false);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchend', handleUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchend', handleUp);
    };
  }, [isDragging, updateScrub]);

  const skipBack = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, audio.currentTime - 15);
  }, []);

  const skipForward = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.min(dur, audio.currentTime + 15);
  }, [dur]);

  const pct = dur > 0 ? (cur / dur) * 100 : 0;

  return (
    <div
      data-screen-label="02 Player"
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        background: '#1F1F1F',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <audio ref={audioRef} src={data.video_url} preload="metadata" style={{ display: 'none' }} />

      {/* Top bar */}
      <div
        style={{
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
          paddingBottom: 8,
          paddingLeft: 8,
          paddingRight: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <button
          onClick={onBack}
          aria-label="Back"
          style={{
            width: 44,
            height: 44,
            background: 'transparent',
            border: 'none',
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
            <path
              d="M3 5.5l5 5 5-5"
              stroke="#fff"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <OpenInAppPill dark compact videoId={data.video_id} />
      </div>

      {/* Cover */}
      <div style={{ padding: '20px 32px 0', display: 'flex', justifyContent: 'center' }}>
        <div
          style={{
            width: 'min(72vw, 280px)',
            aspectRatio: '1/1',
            borderRadius: 22,
            background: '#F7A169',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 16px 48px rgba(0,0,0,0.45)',
          }}
        >
          <Wave color="rgba(255,255,255,0.92)" size={96} />
        </div>
      </div>

      {/* Title */}
      <div style={{ padding: '32px 28px 0', textAlign: 'center' }}>
        <div style={{ fontSize: 19, fontWeight: 600, lineHeight: 1.3, textWrap: 'balance' }}>
          {data.title}
        </div>
        <div style={{ fontSize: 13, color: '#BDBDBD', marginTop: 6 }}>{data.nickname}</div>
      </div>

      {/* Progress */}
      <div style={{ padding: '28px 28px 0' }}>
        <div
          ref={barRef}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          style={{
            position: 'relative',
            height: 24,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              height: 3,
              borderRadius: 2,
              background: 'rgba(255,255,255,0.22)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: 0,
              width: `${pct}%`,
              height: 3,
              borderRadius: 2,
              background: '#fff',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: `calc(${pct}% - 7px)`,
              width: 14,
              height: 14,
              borderRadius: '50%',
              background: '#fff',
            }}
          />
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 10,
            fontSize: 11,
            color: '#BDBDBD',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <span>{fmt(cur)}</span>
          <span>-{fmt(Math.max(0, dur - cur))}</span>
        </div>
      </div>

      {/* Controls */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 36,
          padding: '32px 24px 0',
        }}
      >
        <button
          onClick={skipBack}
          aria-label="Back 15"
          style={{
            width: 48,
            height: 48,
            background: 'transparent',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <path
              d="M14 6V2L8 6l6 4V7a7 7 0 11-7 7"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <text x="14" y="17" textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff" fontFamily="system-ui">
              15
            </text>
          </svg>
        </button>
        <button
          onClick={togglePlay}
          aria-label="Play/Pause"
          style={{
            width: 72,
            height: 72,
            borderRadius: 22,
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'rgba(255,255,255,0.08)',
            backdropFilter: 'blur(14px) saturate(180%)',
            WebkitBackdropFilter: 'blur(14px) saturate(180%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          {isPlaying ? (
            <svg width="28" height="28" viewBox="0 0 26 26" fill="#fff">
              <rect x="6" y="4" width="5" height="18" rx="1.5" />
              <rect x="15" y="4" width="5" height="18" rx="1.5" />
            </svg>
          ) : (
            <svg width="28" height="28" viewBox="0 0 26 26" fill="#fff">
              <path d="M6 3v20l17-10L6 3z" />
            </svg>
          )}
        </button>
        <button
          onClick={skipForward}
          aria-label="Forward 15"
          style={{
            width: 48,
            height: 48,
            background: 'transparent',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <path
              d="M14 6V2l6 4-6 4V7a7 7 0 107 7"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <text x="14" y="17" textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff" fontFamily="system-ui">
              15
            </text>
          </svg>
        </button>
      </div>

      <div style={{ flex: 1 }} />

      {/* Footer — Listen in App CTA */}
      <div
        style={{
          padding: '28px 24px calc(env(safe-area-inset-bottom, 0px) + 26px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <button
          onClick={openApp}
          style={{
            width: '100%',
            maxWidth: 360,
            boxSizing: 'border-box',
            height: 54,
            borderRadius: 27,
            border: 'none',
            background: T.primary,
            color: '#fff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            fontSize: 15.5,
            fontWeight: 600,
            boxShadow: '0 10px 24px rgba(255,145,77,0.35)',
            cursor: 'pointer',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 2v9M4 7l4 4 4-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M3 13.5h10" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <span>Listen in the Oasis app</span>
        </button>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
          Full transcript, chat &amp; more — only in the app
        </div>
      </div>
    </div>
  );
}
